import React, { useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { GradientText } from '../index';
import './card-carousel.styles.scss';

const CardCarousel = ({
  heading,
  subheading,
  items = [],
  className = '',
  variant = 'default',
  autoScroll = true,
  autoScrollSpeed = 0.5,
  type = 'card', // 'card' or 'logo'
  fadeToTransparent = false // For transparent backgrounds
}) => {
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const itemRefs = useRef([]);
  const animationRef = useRef(null);
  const isHovered = useRef(false);
  const manualScrollOffset = useRef(0);
  const isManualScrolling = useRef(false);
  const manualScrollTimeout = useRef(null);

  itemRefs.current = [];

  const addToRefs = (el) => {
    if (el && !itemRefs.current.includes(el)) {
      itemRefs.current.push(el);
    }
  };

  // Get card width based on current viewport
  const getCardWidth = useCallback(() => {
    if (typeof window === 'undefined') return type === 'logo' ? 350 : 320;

    const width = window.innerWidth;
    if (type === 'logo') {
      if (width <= 480) return 250; // Mobile
      if (width <= 768) return 280; // Small tablet
      if (width <= 1024) return 320; // Tablet
      return 350; // Desktop
    } else {
      if (width <= 480) return 240; // Mobile
      if (width <= 768) return 260; // Small tablet
      if (width <= 1024) return 280; // Tablet
      return 320; // Desktop
    }
  }, [type]);

  const renderIcon = (icon, className) => {
    if (typeof icon === 'string' && icon.startsWith('http')) {
      return <img src={icon} alt="" />;
    } else if (typeof icon === 'function') {
      const IconComponent = icon;
      return <IconComponent className={className} />;
    } else {
      return icon;
    }
  };

  const startTrueInfiniteScroll = useCallback(() => {
    if (!autoScroll || !scrollRef.current || items.length === 0) return;

    const scrollContainer = scrollRef.current;
    const currentCardWidth = getCardWidth();
    const cardWidth = currentCardWidth + 16;
    const oneSetWidth = cardWidth * items.length;
    let currentX = 0;

    const moveCarousel = () => {
      // Only auto-scroll if not hovered and not manually scrolling
      if (isHovered.current || isManualScrolling.current) return;

      // Move continuously at consistent speed (1px per frame ≈ 60px/second)
      currentX -= autoScrollSpeed;

      // Apply the transform combining auto-scroll and manual offset
      const totalX = currentX + manualScrollOffset.current;
      scrollContainer.style.transform = `translateX(${totalX}px)`;

      if (currentX <= -oneSetWidth) {
        currentX = 0;
      }
    };

    const updateManualPosition = () => {
      if (!isManualScrolling.current) return;

      // Apply combined position (auto + manual scrolling)
      const totalX = currentX + manualScrollOffset.current;
      scrollContainer.style.transform = `translateX(${totalX}px)`;

      // Keep manual offset normalized to prevent drift
      // This ensures manual scrolling also respects the infinite belt
      while (manualScrollOffset.current > oneSetWidth) {
        manualScrollOffset.current -= oneSetWidth;
      }
      while (manualScrollOffset.current < -oneSetWidth) {
        manualScrollOffset.current += oneSetWidth;
      }
    };

    // Use requestAnimationFrame for smooth 60fps animation
    const animate = () => {
      moveCarousel();
      updateManualPosition();
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
  }, [autoScroll, items.length, getCardWidth, autoScrollSpeed]);

  const stopInfiniteScroll = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    if (type !== 'logo') {
      isHovered.current = true;
    }
  };

  const handleMouseLeave = () => {
    if (type !== 'logo') {
      isHovered.current = false;
    }
  };

  const handleWheel = (e) => {
    // Disable manual scrolling for logo carousels
    if (type === 'logo') return;

    // Only handle horizontal scrolling
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();

      // Add the scroll delta to our manual offset (negate to fix direction)
      manualScrollOffset.current -= e.deltaX;
      isManualScrolling.current = true;

      // Clear existing timeout
      if (manualScrollTimeout.current) {
        clearTimeout(manualScrollTimeout.current);
      }

      // Resume auto-scroll after a delay
      manualScrollTimeout.current = setTimeout(() => {
        isManualScrolling.current = false;
      }, 1500); // Resume auto-scroll 1.5 seconds after last manual scroll
    }
  };

  // GSAP entrance animations
  useEffect(() => {
    if (typeof window !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);

      // For logo carousels, make items visible immediately without animation
      if (type === 'logo') {
        const cards = scrollRef.current?.querySelectorAll('.card-carousel-item');
        if (cards) {
          cards.forEach((card) => {
            card.style.opacity = '1';
            card.style.transform = 'translateX(0px) scale(1)';
          });
        }
        return;
      }

      const timer = setTimeout(() => {
        if (itemRefs.current.length > 0) {
          gsap.fromTo(
            itemRefs.current,
            {
              opacity: 0,
              x: 50,
              scale: 0.95
            },
            {
              opacity: 1,
              x: 0,
              scale: 1,
              duration: 0.25,
              stagger: 0.1,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: containerRef.current,
                start: 'top 100%',
                toggleActions: 'play none none reverse'
              }
            }
          );
        } else {
          // Fallback: if no refs, make all cards visible immediately
          const fallbackTimer = setTimeout(() => {
            const cards = scrollRef.current?.querySelectorAll('.card-carousel-item');
            if (cards) {
              cards.forEach((card) => {
                card.style.opacity = '1';
                card.style.transform = 'translateX(0px) scale(1)';
              });
            }
          }, 0);

          return () => clearTimeout(fallbackTimer);
        }
      }, 0);

      return () => {
        clearTimeout(timer);
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      };
    }
  }, [items, type]);

  useEffect(() => {
    if (autoScroll && items.length > 0) {
      // For logo carousels, start immediately without delay
      if (type === 'logo') {
        startTrueInfiniteScroll();
        return () => {
          stopInfiniteScroll();
        };
      }

      const startDelay = setTimeout(() => {
        startTrueInfiniteScroll();
      }, 0);

      return () => {
        clearTimeout(startDelay);
        stopInfiniteScroll();
      };
    }
  }, [autoScroll, items.length, startTrueInfiniteScroll, autoScrollSpeed, type]);

  // Handle resize events to restart infinite scroll with correct calculations
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      if (autoScroll && items.length > 0) {
        stopInfiniteScroll();
        // Restart with a small delay to let layout settle
        setTimeout(() => {
          startTrueInfiniteScroll();
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      stopInfiniteScroll();
      if (manualScrollTimeout.current) {
        clearTimeout(manualScrollTimeout.current);
      }
    };
  }, [autoScroll, items.length, startTrueInfiniteScroll, autoScrollSpeed]);

  return (
    <div ref={containerRef} className={`card-grid-container ${className}`}>
      {/* Header */}
      {(heading || subheading) && (
        <div className="card-grid-header">
          {heading && (
            <GradientText variant="lightGrey" className="card-grid-heading fs-h2">
              {heading}
            </GradientText>
          )}
          {subheading && <p className="card-grid-subheading">{subheading}</p>}
        </div>
      )}

      {/* Infinite Carousel */}
      <div
        className={`card-carousel-wrapper ${fadeToTransparent ? 'card-carousel-wrapper--transparent' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
      >
        <div
          ref={scrollRef}
          className={`card-carousel card-carousel--${variant} ${type === 'logo' ? 'card-carousel--logo' : ''}`}
        >
          {[...Array(3)].map((_, setIndex) => (
            <React.Fragment key={`set-${setIndex}`}>
              {items.map((item, itemIndex) => (
                <div
                  key={`infinite-${setIndex}-${itemIndex}`}
                  ref={addToRefs}
                  className={`card-carousel-item ${type === 'logo' ? 'card-carousel-item--logo' : ''}`}
                >
                  {type === 'logo' ? (
                    <div className="card-carousel-logo-container">
                      <img
                        src={item.src || item}
                        alt={item.alt || ''}
                        className="card-carousel-logo"
                      />
                    </div>
                  ) : (
                    <div className="card-carousel-item-content">
                      {item.icon && (
                        <div className="card-carousel-item-icon">
                          {renderIcon(item.icon, 'card-carousel-icon-lucide')}
                        </div>
                      )}
                      <h3 className="card-carousel-item-title fs-h6">{item.title}</h3>
                      {item.description && (
                        <p className="card-carousel-item-description fs-p">{item.description}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>

        {/* Gradient fade indicators */}
        <div
          className={`card-carousel-fade-left ${fadeToTransparent ? 'card-carousel-fade-transparent' : ''}`}
        ></div>
        <div
          className={`card-carousel-fade-right ${fadeToTransparent ? 'card-carousel-fade-transparent' : ''}`}
        ></div>
      </div>
    </div>
  );
};

export default CardCarousel;
